import { auth, db, storage, rdb } from "../config";
import {
  ref as refDB,
  serverTimestamp as rtdbServerTimestamp,
  update,
} from "firebase/database";
import {
  createUserWithEmailAndPassword,
  EmailAuthProvider,
  GoogleAuthProvider,
  OAuthProvider,
  reauthenticateWithCredential,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  updateEmail,
  updatePassword,
  type User,
} from "firebase/auth";
import {
  doc,
  setDoc,
  serverTimestamp,
  updateDoc,
  getDoc,
  arrayUnion,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import {
  deleteObject,
  getDownloadURL,
  ref,
  uploadBytes,
} from "firebase/storage";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  CreateUserResult,
  LoginRequest,
  LoginResult,
  UserRecord,
  RequestPasswordResetResult,
  PublicPhoto,
} from "./user.types";
import { IndicationRequest } from "../indications/indications.types";
import { AnalyticsMetrics } from "../analytics/analyticsService";
import { slugify } from "@/utils";

export class UserService {
  translateFirebaseError = (error: any): string => {
    const code = error?.code || "";

    switch (code) {
      // 🔐 ERROS DE CREDENCIAIS (O que está faltando!)
      case "auth/invalid-credential":
      case "auth/wrong-password":
      case "auth/user-not-found":
        return "E-mail ou senha incorretos. Verifique os dados e tente novamente.";

      case "auth/too-many-requests":
        return "Muitas tentativas malsucedidas. Tente novamente em alguns minutos ou redefina sua senha.";

      // 📧 ERROS DE CADASTRO
      case "auth/email-already-in-use":
        return "Este e-mail já está cadastrado. Tente fazer login ou recupere sua senha.";
      case "auth/invalid-email":
        return "O endereço de e-mail não é válido.";
      case "auth/weak-password":
        return "A senha é muito fraca. Use pelo menos 6 caracteres.";

      // 🌐 ERROS DE REDE/SISTEMA
      case "auth/network-request-failed":
        return "Falha na conexão. Verifique sua internet.";
      case "auth/user-disabled":
        return "Esta conta foi desativada por um administrador.";
      case "auth/operation-not-allowed":
        return "O login por e-mail e senha não está habilitado.";

      default:
        console.warn("Erro não mapeado:", code);
        return "Ocorreu um erro inesperado. Tente novamente mais tarde.";
    }
  };

  extractStoragePath = (photoUrl: string, userId: string) => {
    try {
      const url = new URL(photoUrl);
      const encodedPath = url.pathname.split("/o/")[1];

      if (!encodedPath) return null;

      const fullPath = decodeURIComponent(encodedPath);
      const prefix = `users/${userId}/`;

      return fullPath.startsWith(prefix) ? fullPath.replace(prefix, "") : null;
    } catch (err) {
      console.error("Erro ao extrair caminho do storage", err);

      return null;
    }
  };

  async compressImage(
    file: File,
    quality = 0.7,
    options?: { maxDimension?: number },
  ): Promise<File> {
    return new Promise((resolve, reject) => {
      const img = new window.Image();
      const reader = new FileReader();
      reader.onload = (e) => {
        img.src = e.target?.result as string;
        img.onload = () => {
          const canvas = document.createElement("canvas");

          const maxDimension = options?.maxDimension ?? 1280;
          const originalWidth = img.width || 0;
          const originalHeight = img.height || 0;

          if (!originalWidth || !originalHeight) {
            return reject("Erro ao processar imagem.");
          }

          const scale = Math.min(
            1,
            maxDimension / Math.max(originalWidth, originalHeight),
          );

          canvas.width = Math.round(originalWidth * scale);
          canvas.height = Math.round(originalHeight * scale);
          const ctx = canvas.getContext("2d");
          if (!ctx) return reject("Erro ao processar imagem.");
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          canvas.toBlob(
            (blob) => {
              if (!blob) return reject("Erro ao comprimir arquivo.");

              // Garante MIME válido para passar nas rules (image/.*)
              const mimeType = blob.type || "image/jpeg";
              const baseName = file.name?.replace(/\.[^/.]+$/, "") || "image";
              const outputName = `${baseName}.jpg`;

              resolve(new File([blob], outputName, { type: mimeType }));
            },
            "image/jpeg",
            quality,
          );
        };
      };
      reader.onerror = () => reject("Erro ao ler arquivo.");
      reader.readAsDataURL(file);
    });
  }

  isLoggedIn(user?: User | null): boolean {
    if (typeof user !== "undefined") {
      return Boolean(user);
    }
    return Boolean(auth.currentUser);
  }

  async createUser(
    data: UserRecord,
    options?: {
      profilePhoto?: File | null;
      userIndication?: IndicationRequest | null;
    },
  ): Promise<CreateUserResult> {
    try {
      if (!data.email || !data.password || !data.username) {
        return {
          success: false,
          error: {
            title: "Dados incompletos.",
            detail: "E-mail, senha e nome de usuário são obrigatórios.",
            status: 400,
          },
        };
      }

      const cleanUsername = data.username?.trim();

      const userCredential = await createUserWithEmailAndPassword(
        auth,
        data.email,
        data.password,
      );

      const userId = userCredential.user.uid;
      const userDocRef = doc(db, "users", userId);
      const usernameSlug = slugify(data.username);

      const payload = {
        uid: userId,
        email: data.email || null,
        phone: data.phone || null,
        gender: data.gender || null,
        seeking: data.seeking || null,
        acceptedTerms: data.acceptedTerms || false,
        username: cleanUsername,
        usernameSlug: usernameSlug,
        birthDay: data.birthDay || null,
        birthMonth: data.birthMonth || null,
        birthYear: data.birthYear || null,
        country: data.country || null,
        state: data.state || null,
        city: data.city || null,
        referralSource: data.referralSource || null,
        bodyType: data.bodyType || null,
        skinTone: data.skinTone || null,
        hairColor: data.hairColor || null,
        eyeColor: data.eyeColor || null,
        height: data.height || null,
        smoking: data.smoking || null,
        drinking: data.drinking || null,
        maritalStatus: data.maritalStatus || null,
        children: data.children || null,
        education: data.education || null,
        profession: data.profession || null,
        bioTitle: data.bioTitle || null,
        bioDescription: data.bioDescription || null,
        travelPreference: data.travelPreference || null,
        seekingDescription: data.seekingDescription || null,
        emailVerified: false,
        awaitingApproval: true,
        visible: true,
        userDisapproved: false,
        closeAccount: false,
        createdAt: serverTimestamp(),
      };

      await Promise.all([setDoc(userDocRef, payload)]);

      if (options?.profilePhoto) {
        try {
          await this.uploadProfilePhoto(userId, options.profilePhoto);
        } catch (err) {
          console.error("Erro ao fazer upload da foto:", err);
        }
      }

      if (options?.userIndication) {
        try {
          const indicationData = {
            ...options.userIndication,
            referredUid: userId,
          };

          await this.registerIndication(indicationData);
        } catch (indErr) {
          console.error("⚠️ Erro:", indErr);
        }
      }

      await sendEmailVerification(userCredential.user);
      await signOut(auth);

      AnalyticsMetrics.trackRegisterSuccess(userId);

      return {
        success: true,
        uid: userId,
      };
    } catch (error: any) {
      const detail = this.translateFirebaseError(error);

      AnalyticsMetrics.trackError("register_error", detail);

      return {
        success: false,
        error: {
          title: "Não foi possível criar sua conta.",
          detail,
          status: 400,
        },
      };
    }
  }

  async completeCreateUser(
    userId: string,
    email: string | null,
    phone: string | null,
    data: UserRecord,
    options?: {
      profilePhoto?: File | null;
      userIndication?: IndicationRequest | null;
    },
  ): Promise<CreateUserResult> {
    try {
      const userDocRef = doc(db, "users", userId);
      const usernameSlug = slugify(data.username || "");

      const payload = {
        uid: userId,
        email: email || null,
        phone: phone || data.phone || null,
        gender: data.gender || null,
        seeking: data.seeking || null,
        acceptedTerms: true, // Já aceitou os termos no fluxo do login com o Google
        username: data.username?.trim(),
        usernameSlug: usernameSlug,
        birthDay: data.birthDay || null,
        birthMonth: data.birthMonth || null,
        birthYear: data.birthYear || null,
        country: data.country || "Brasil",
        state: data.state || null,
        city: data.city || null,
        referralSource: data.referralSource || null,
        bodyType: data.bodyType || null,
        skinTone: data.skinTone || null,
        hairColor: data.hairColor || null,
        eyeColor: data.eyeColor || null,
        height: data.height || null,
        smoking: data.smoking || null,
        drinking: data.drinking || null,
        maritalStatus: data.maritalStatus || null,
        children: data.children || null,
        education: data.education || null,
        profession: data.profession || null,
        bioTitle: data.bioTitle || null,
        bioDescription: data.bioDescription || null,
        travelPreference: data.travelPreference || null,
        seekingDescription: data.seekingDescription || null,
        emailVerified: email ? true : false, // Provedor Google já é verificado nativamente
        awaitingApproval: true,
        visible: true,
        userDisapproved: false,
        closeAccount: false,
        createdAt: serverTimestamp(),
      };

      await setDoc(userDocRef, payload);

      if (options?.profilePhoto) {
        try {
          await this.uploadProfilePhoto(userId, options.profilePhoto);
        } catch (err) {
          console.error("Erro ao fazer upload da foto social:", err);
        }
      }

      if (options?.userIndication) {
        try {
          await this.registerIndication({
            ...options.userIndication,
            referredUid: userId,
          });
        } catch (indErr) {
          console.error("Erro ao computar indicação social:", indErr);
        }
      }

      AnalyticsMetrics.trackRegisterSuccess(userId);

      return { success: true, uid: userId };
    } catch (error: any) {
      const detail = this.translateFirebaseError(error);
      AnalyticsMetrics.trackError("social_register_error", detail);

      return {
        success: false,
        error: {
          title: "Não foi possível concluir seu perfil.",
          detail,
          status: 400,
        },
      };
    }
  }

  async checkUsernameExists(username: string): Promise<boolean> {
    try {
      const cleanUsername = username.trim();
      if (!cleanUsername) return false;

      const functions = getFunctions();
      const checkUsernameExists = httpsCallable(
        functions,
        "checkUsernameExists",
      );
      const result: any = await checkUsernameExists({
        username: cleanUsername,
      });

      return result.data.exists; // Retorna true se já existir
    } catch (error) {
      console.error("Erro ao verificar username:", error);
      return false;
    }
  }

  async logout() {
    const uid = auth.currentUser?.uid;

    if (uid) {
      const userStatusRef = refDB(rdb, `status/${uid}`);

      await update(userStatusRef, {
        state: "offline",
        lastChanged: rtdbServerTimestamp(),
      });
    }

    await signOut(auth);
  }

  async resetPassword(email: string): Promise<RequestPasswordResetResult> {
    try {
      await sendPasswordResetEmail(auth, email);
      return { success: true };
    } catch (error: any) {
      return {
        success: false,
        error: {
          type: "about:blank",
          title: "Erro ao enviar e-mail",
          status: 500,
          detail: this.translateFirebaseError(error),
        },
      };
    }
  }

  async changeEmail(params: {
    currentEmail: string;
    currentPassword: string;
    newEmail: string;
  }): Promise<{ success: boolean; error?: { detail: string } }> {
    try {
      const user = auth.currentUser;
      if (!user || !user.email) throw new Error("auth/user-not-found");

      const { currentEmail, currentPassword, newEmail } = params;
      const credential = EmailAuthProvider.credential(
        currentEmail,
        currentPassword,
      );

      await reauthenticateWithCredential(user, credential);
      await updateEmail(user, newEmail);
      await updateDoc(doc(db, "users", user.uid), {
        email: newEmail,
        updatedAt: serverTimestamp(),
      });
      return { success: true };
    } catch (error: any) {
      return {
        success: false,
        error: { detail: this.translateFirebaseError(error) },
      };
    }
  }

  async changePassword(params: {
    currentPassword: string;
    newPassword: string;
  }): Promise<{ success: boolean; error?: { detail: string } }> {
    try {
      const user = auth.currentUser;
      if (!user || !user.email) throw new Error("auth/user-not-found");

      const { currentPassword, newPassword } = params;
      const credential = EmailAuthProvider.credential(
        user.email,
        currentPassword,
      );

      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);
      await updateDoc(doc(db, "users", user.uid), {
        updatedAt: serverTimestamp(),
      });
      return { success: true };
    } catch (error: any) {
      return {
        success: false,
        error: { detail: this.translateFirebaseError(error) },
      };
    }
  }

  async sendVerificationEmail(
    user?: User,
  ): Promise<{ success: boolean; error?: string }> {
    try {
      // Se o usuário foi passado (via login), usa ele. Senão, usa o logado.
      const targetUser = user || auth.currentUser;
      if (!targetUser) throw new Error("Usuário não autenticado.");

      await sendEmailVerification(targetUser);
      return { success: true };
    } catch (error: any) {
      return { success: false, error: this.translateFirebaseError(error) };
    }
  }

  async resendVerificationEmail(params: {
    email: string;
    password: string;
  }): Promise<{ success: boolean; error?: string }> {
    let signedInUser: User | null = null;

    try {
      const { email, password } = params;
      if (!email || !password) {
        return {
          success: false,
          error: "Informe e-mail e senha para reenviar a verificação.",
        };
      }

      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password,
      );

      signedInUser = userCredential.user;

      // Garante que `emailVerified` reflita o estado atual
      try {
        await signedInUser.reload();
        await signedInUser.getIdToken(true);
      } catch {
        // noop
      }

      if (signedInUser.emailVerified) {
        return {
          success: false,
          error: "Seu e-mail já está verificado.",
        };
      }

      await sendEmailVerification(signedInUser);
      return { success: true };
    } catch (error: any) {
      return { success: false, error: this.translateFirebaseError(error) };
    } finally {
      // Garante que não deixamos uma sessão autenticada aberta no cliente.
      if (signedInUser) {
        try {
          await signOut(auth);
        } catch {
          // noop
        }
      }
    }
  }

  async login(data: LoginRequest): Promise<LoginResult> {
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        data.email,
        data.password,
      );

      const { user } = userCredential;

      try {
        await user.reload();
        await user.getIdToken(true);
      } catch (reloadError) {
        console.error("Erro ao recarregar usuário:", reloadError);
      }

      if (!user.emailVerified) {
        await signOut(auth);
        return {
          success: false,
          error: {
            type: "about:blank",
            title: "E-mail não verificado",
            status: 403,
            detail:
              "Por favor, verifique sua caixa de entrada e confirme seu e-mail antes de acessar.",
          },
        };
      }

      const userDocRef = doc(db, "users", user.uid);

      try {
        await updateDoc(userDocRef, {
          emailVerified: true,
          updatedAt: serverTimestamp(),
        });
      } catch (syncError) {
        console.warn("Sincronização preventiva falhou, tentando prosseguir...");
      }

      const snapshot = await getDoc(userDocRef);

      if (!snapshot.exists()) {
        // Caso raro: Auth existe mas Firestore não (erro de propagação no cadastro)
        await signOut(auth);
        return {
          success: false,
          error: {
            type: "about:blank",
            title: "Perfil não encontrado",
            status: 404,
            detail:
              "Seu perfil ainda está sendo processado. Tente novamente em instantes.",
          },
        };
      }

      const userData = snapshot.data();

      if (userData.closeAccount) {
        await signOut(auth);
        return {
          success: false,
          error: {
            type: "about:blank",
            title: "Conta Encerrada",
            status: 403,
            detail: "Esta conta foi encerrada pelo usuário.",
          },
        };
      }

      AnalyticsMetrics.trackUserLoggedIn(user.uid, "email_password");

      return { success: true, user: user, isNewUser: false };
    } catch (error: any) {
      // Se o erro for 'permission-denied' aqui, é porque o token verificado
      // ainda não foi aceito pelo Firestore ou a regra de segurança barrou.
      const detail = this.translateFirebaseError(error);

      AnalyticsMetrics.trackError("login_error", detail);

      return {
        success: false,
        error: {
          type: "about:blank",
          title: "Erro no acesso",
          status: 500,
          detail,
        },
      };
    }
  }

  async loginWithGoogle(): Promise<LoginResult> {
    try {
      const provider = new GoogleAuthProvider();
      provider.addScope("openid");
      provider.addScope("profile");
      provider.addScope("email");

      const userCredential = await signInWithPopup(auth, provider);
      const { user } = userCredential;

      const userDocRef = doc(db, "users", user.uid);
      const snapshot = await getDoc(userDocRef);

      if (snapshot.exists()) {
        const userData = snapshot.data() as UserRecord;

        if (userData.closeAccount) {
          await signOut(auth);
          return {
            success: false,
            error: {
              title: "Conta Encerrada",
              detail: "Esta conta foi encerrada pelo usuário.",
              status: 403,
            },
          };
        }

        // Se a conta já existe no Firestore mas o e-mail não constava
        // como verificado, atualizamos agora, pois o OAuth da Google atesta que o e-mail é válido.
        if (!userData.emailVerified) {
          try {
            await updateDoc(userDocRef, {
              emailVerified: true,
              updatedAt: serverTimestamp(),
            });

            userData.emailVerified = true;
          } catch (syncError) {
            console.warn(
              "Falha ao atualizar verificação de e-mail no Firestore:",
              syncError,
            );
          }
        }

        AnalyticsMetrics.trackUserLoggedIn(user.uid, "google");
        return { success: true, user, isNewUser: false };
      }

      // VERIFICAÇÃO SECUNDÁRIA: Só roda se o documento no Firestore NÃO existir por completo
      if (user.email) {
        const usersRef = collection(db, "users");
        const q = query(usersRef, where("email", "==", user.email));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          await signOut(auth);
          return {
            success: false,
            error: {
              title: "Conta já existente.",
              detail:
                "Este e-mail já está cadastrado. Por favor, faça login utilizando o método original.",
              status: 409,
            },
          };
        }
      }

      return { success: true, user, isNewUser: true };
    } catch (error: any) {
      const detail = this.translateFirebaseError(error);
      AnalyticsMetrics.trackError("google_login_error", detail);
      return {
        success: false,
        error: {
          title: "Não foi possível acessar com o Google.",
          detail,
          status: 500,
        },
      };
    }
  }

  async loginWithMicrosoft(): Promise<LoginResult> {
    try {
      const provider = new OAuthProvider("microsoft.com");

      provider.addScope("openid");
      provider.addScope("profile");
      provider.addScope("email");

      const userCredential = await signInWithPopup(auth, provider);
      const { user } = userCredential;

      const userDocRef = doc(db, "users", user.uid);
      const snapshot = await getDoc(userDocRef);

      if (snapshot.exists()) {
        const userData = snapshot.data() as UserRecord;

        if (userData.closeAccount) {
          await signOut(auth);
          return {
            success: false,
            error: {
              title: "Conta Encerrada",
              detail: "Esta conta foi encerrada pelo usuário.",
              status: 403,
            },
          };
        }

        // Se a conta já existe no Firestore mas o e-mail não constava
        // como verificado, atualizamos agora, pois o OAuth da Microsoft atesta que o e-mail é válido.
        if (!userData.emailVerified) {
          try {
            await updateDoc(userDocRef, {
              emailVerified: true,
              updatedAt: serverTimestamp(),
            });
            userData.emailVerified = true;
          } catch (syncError) {
            console.warn(
              "Falha ao atualizar verificação de e-mail (Microsoft) no Firestore:",
              syncError,
            );
          }
        }

        AnalyticsMetrics.trackUserLoggedIn(user.uid, "microsoft");
        return { success: true, user, isNewUser: false };
      }

      // VERIFICAÇÃO SECUNDÁRIA: Só roda se o documento no Firestore NÃO existir por completo
      if (user.email) {
        const usersRef = collection(db, "users");
        const q = query(usersRef, where("email", "==", user.email));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          await signOut(auth);
          return {
            success: false,
            error: {
              title: "Conta já existente.",
              detail:
                "Este e-mail já está cadastrado. Por favor, faça login utilizando o método original.",
              status: 409,
            },
          };
        }
      }

      return { success: true, user, isNewUser: true };
    } catch (error: any) {
      const detail = this.translateFirebaseError(error);
      AnalyticsMetrics.trackError("microsoft_login_error", detail);
      return {
        success: false,
        error: {
          title: "Não foi possível acessar com a conta Microsoft.",
          detail,
          status: 500,
        },
      };
    }
  }

  async closeAccount(): Promise<{
    success: boolean;
    error?: { detail: string };
  }> {
    try {
      const user = auth.currentUser;
      if (!user) throw new Error("auth/user-not-found");

      await updateDoc(doc(db, "users", user.uid), {
        closeAccount: true,
        closeAccountAt: serverTimestamp(),
      });

      await signOut(auth);
      return { success: true };
    } catch (error: any) {
      return {
        success: false,
        error: { detail: this.translateFirebaseError(error) },
      };
    }
  }

  async changeVisibility(visible: boolean): Promise<{
    success: boolean;
    error?: { detail: string };
  }> {
    try {
      const user = auth.currentUser;
      if (!user) throw new Error("auth/user-not-found");

      await updateDoc(doc(db, "users", user.uid), {
        visible,
        updatedAt: serverTimestamp(),
      });

      return { success: true };
    } catch (error: any) {
      return {
        success: false,
        error: { detail: this.translateFirebaseError(error) },
      };
    }
  }

  async uploadProfilePhoto(uid: string, file: File) {
    try {
      if (!uid || !file) throw new Error("Dados obrigatórios ausentes.");

      if (!auth.currentUser) {
        throw { code: "auth/user-not-found" };
      }

      const compressed = await this.compressImage(file, 0.7);
      const storageRef = ref(storage, `users/${uid}/profile/photos/profile`);
      const snapshot = await uploadBytes(storageRef, compressed, {
        contentType: compressed.type || "image/jpeg",
      });
      const downloadURL = await getDownloadURL(snapshot.ref);

      const newPhoto: PublicPhoto = {
        url: downloadURL,
        approved: false,
        createdAt: new Date().toISOString(),
      };

      await updateDoc(doc(db, "users", uid), {
        profileImage: newPhoto,
        updatedAt: serverTimestamp(),
      });

      return newPhoto;
    } catch (error: any) {
      throw new Error(this.translateFirebaseError(error));
    }
  }

  async addPublicPhoto(uid: string, file: File) {
    try {
      const compressed = await this.compressImage(file, 0.7);
      const fileName = `${Date.now()}_${file.name}`;
      const storageRef = ref(
        storage,
        `users/${uid}/profile/photos/publicPhotos/${fileName}`,
      );

      const snapshot = await uploadBytes(storageRef, compressed, {
        contentType: compressed.type || "image/jpeg",
      });

      const downloadURL = await getDownloadURL(snapshot.ref);

      const newPhoto: PublicPhoto = {
        url: downloadURL,
        approved: false,
        createdAt: new Date().toISOString(),
      };

      const userRef = doc(db, "users", uid);
      await updateDoc(userRef, {
        publicPhotos: arrayUnion(newPhoto),
        updatedAt: serverTimestamp(),
      });

      return newPhoto;
    } catch (error: any) {
      throw new Error(this.translateFirebaseError(error));
    }
  }

  async deleteProfilePhoto(userId: string, fileName: string) {
    try {
      const storageRef = ref(storage, `users/${userId}/${fileName}`);
      try {
        await deleteObject(storageRef);
      } catch (error: any) {
        if (error.code !== "storage/object-not-found") throw error;
      }

      if (fileName === "profile/photos/profile") {
        await updateDoc(doc(db, "users", userId), {
          profileImage: null,
          updatedAt: serverTimestamp(),
        });
      }
      return true;
    } catch (error: any) {
      throw new Error(this.translateFirebaseError(error));
    }
  }

  async deletePublicPhoto(
    userId: string,
    fileName: string,
    updatedPhotosList: PublicPhoto[],
  ) {
    try {
      const storageRef = ref(storage, `users/${userId}/${fileName}`);
      try {
        await deleteObject(storageRef);
      } catch (error: any) {
        if (error.code !== "storage/object-not-found") throw error;
      }

      await updateDoc(doc(db, "users", userId), {
        publicPhotos: updatedPhotosList,
        updatedAt: serverTimestamp(),
      });

      return true;
    } catch (error: any) {
      throw new Error(this.translateFirebaseError(error));
    }
  }

  private generateReferralCode(slug: string): string {
    const prefix = slug.split("-")[0].substring(0, 4).toUpperCase();
    const randomChars = Math.random()
      .toString(36)
      .substring(2, 6)
      .toUpperCase();
    return `${prefix}-${randomChars}`;
  }

  async getOrCreateReferralCode(
    uid: string,
    usernameSlug: string,
  ): Promise<{ usernameSlug: string; referralCode: string }> {
    try {
      const userDocRef = doc(db, "users", uid);
      const snapshot = await getDoc(userDocRef);

      if (!snapshot.exists()) {
        throw new Error("Usuário não encontrado");
      }

      const userData = snapshot.data();

      if (userData.referralCode) {
        return {
          usernameSlug: userData.usernameSlug,
          referralCode: userData.referralCode,
        };
      }

      const newCode = this.generateReferralCode(usernameSlug);

      await updateDoc(userDocRef, {
        referralCode: newCode,
        updatedAt: serverTimestamp(),
      });

      return {
        usernameSlug: usernameSlug,
        referralCode: newCode,
      };
    } catch (error: any) {
      console.error("Erro ao gerenciar referralCode:", error);
      throw new Error("Erro ao processar link de indicação.");
    }
  }

  /**
   * Registra o vínculo de indicação na coleção 'users_indications'
   */
  async registerIndication(data: IndicationRequest): Promise<void> {
    try {
      if (!data.referrerSlug && !data.referralCode) {
        console.warn(
          "⚠️ Tentativa de registrar indicação sem dados de origem.",
        );
        return;
      }

      const indicationRef = doc(collection(db, "users_indications"));
      const { referrerSlug, referralCode, referredUid, referredUsername } =
        data;
      const COMMISION_VALUE = Number(
        process.env.REACT_APP_COMMISION_VALUE ?? 0,
      );

      await setDoc(indicationRef, {
        id: indicationRef.id,
        referrerSlug: referrerSlug || null, // Quem indicou (slug)
        referralCode: referralCode || null, // Código usado
        referredUid: referredUid, // Quem foi indicado (ID novo)
        referredUsername: referredUsername || "", // Nome de quem entrou
        createdAt: serverTimestamp(),
        commissionValue: COMMISION_VALUE, // Comissão fixa por indicação paga
        status: "pending", // Para validar depois se o usuário pagou/assinou
      });
    } catch (error) {
      console.error("Erro ao salvar registro de indicação:", error);
    }
  }
}

export const userService = new UserService();
